import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  FileUp,
  FolderOpen,
  Loader2,
  Upload,
  X,
} from 'lucide-react';
import {
  MAX_UPLOAD_FILES,
  isUploadFileAllowed,
  uploadAccept,
  uploadAcceptLabel,
} from '@peles/shared';
import type { UploadedFileRef, UploadLinkInfo } from '@peles/shared';
import { AcceptedFileTypesNote } from '../components/common/AcceptedFileTypesNote';
import { formatFileSize } from '../utils/fileSize';
import {
  UploadLinkError,
  finalizeUpload,
  getUploadLinkInfo,
  getUploadUrl,
} from '../api/publicUpload';

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

type Phase = 'loading' | 'ready' | 'not-found' | 'uploading' | 'done' | 'used';

type FileState = 'waiting' | 'uploading' | 'uploaded' | 'failed';

/** A presign kept for a retry: reused while fresh, so a retry spends none. */
interface UploadTicket {
  uploadUrl: string;
  s3Key?: string;
  fileId?: string;
  issuedAt: number;
}

/** Presigned URLs live longer than this; past it a retry asks for a new one. */
const TICKET_REUSE_MS = 10 * 60 * 1000;

/** One chosen file, and how far it has got. */
interface PendingFile {
  key: string;
  file: File;
  state: FileState;
  /** The presign its transfer used — a failed transfer is retried on it. */
  ticket?: UploadTicket;
  /** Set once its bytes are stored — a retry never uploads it twice. */
  ref?: UploadedFileRef;
  error?: string;
}

/*
 * ⚠️ **A key per pick, never one read off the file.** Two different files can
 * share a name, size and modified time (the same file name in two folders,
 * exported a moment apart); a key built from those skipped the second without
 * a word and the link was spent with one. Only the very same `File` chosen
 * twice is a duplicate.
 */
let nextFileKey = 0;
function newFileKey(): string {
  nextFileKey += 1;
  return `file-${nextFileKey}`;
}

/**
 * Public, tokenized page where anybody the office asked — a vendor, a client —
 * sends files about a record. No login: the token in the URL is the
 * capability. Several files go in one submission, which spends the link; each
 * is uploaded on its own first, so a failure costs that file's retry, never the
 * whole batch.
 */
export function PublicUploadPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [info, setInfo] = useState<UploadLinkInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [sentNames, setSentNames] = useState<string[]>([]);

  useEffect(() => {
    let active = true;
    getUploadLinkInfo(token)
      .then((data) => {
        if (!active) return;
        setInfo(data);
        // Single-use link: a consumed token can't upload again.
        setPhase(data.used ? 'used' : 'ready');
      })
      .catch(() => {
        if (!active) return;
        setPhase('not-found');
      });
    return () => {
      active = false;
    };
  }, [token]);

  const allowedTypes = info?.allowedFileTypes ?? [];
  // A server from before Phase 9 sends no `maxFiles`; it took one file.
  const maxFiles = Math.min(info?.maxFiles ?? 1, MAX_UPLOAD_FILES);

  function addFiles(chosen: File[]) {
    setError(null);
    /*
     * ⚠️ **Checked here as well as on the server, and neither is redundant.**
     * The `accept` attribute only filters a file dialog — a file can be dropped
     * past it — so without this a guest could pick a wrong file, wait out the
     * upload, and be refused at the end. The server still refuses too, because
     * this check runs on the guest's machine and is not a control.
     */
    const problems: string[] = [];
    const next = [...files];
    for (const file of chosen) {
      if (next.some((f) => f.file === file)) continue;
      if (!isUploadFileAllowed(allowedTypes, file.type, file.name)) {
        problems.push(
          `${file.name}: this link accepts ${uploadAcceptLabel(allowedTypes) ?? 'any file'}.`,
        );
        continue;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        problems.push(`${file.name} is too large (max 25 MB).`);
        continue;
      }
      if (next.length >= maxFiles) {
        problems.push(
          `${file.name} was left out — this link takes up to ${maxFiles} file${maxFiles === 1 ? '' : 's'}.`,
        );
        continue;
      }
      next.push({ key: newFileKey(), file, state: 'waiting' });
    }
    setFiles(next);
    if (problems.length > 0) setError(problems.join(' '));
  }

  function removeFile(key: string) {
    setFiles((prev) => prev.filter((f) => f.key !== key));
  }

  function patchFile(key: string, patch: Partial<PendingFile>) {
    setFiles((prev) => prev.map((f) => (f.key === key ? { ...f, ...patch } : f)));
  }

  /** Store one file's bytes; answers its ref, or null when it failed. */
  async function uploadOne(entry: PendingFile): Promise<UploadedFileRef | null> {
    if (entry.ref) return entry.ref;
    patchFile(entry.key, { state: 'uploading', error: undefined });
    try {
      const mimeType = entry.file.type || 'application/octet-stream';
      const ticket =
        entry.ticket && Date.now() - entry.ticket.issuedAt < TICKET_REUSE_MS
          ? entry.ticket
          : { ...(await getUploadUrl(token, entry.file.name, mimeType)), issuedAt: Date.now() };
      patchFile(entry.key, { ticket });
      const { uploadUrl, s3Key, fileId } = ticket;
      const putResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': mimeType },
        body: entry.file,
      });
      if (!putResponse.ok) throw new Error('Upload failed');
      // Whichever handle the presign gave back, unchanged. A link from a
      // conversation files into the library and answers `fileId`; every other
      // link answers `s3Key`. This page is not the one that decides.
      const ref: UploadedFileRef = {
        ...(s3Key ? { s3Key } : {}),
        ...(fileId ? { fileId } : {}),
        fileName: entry.file.name,
        mimeType,
        fileSizeBytes: entry.file.size,
      };
      patchFile(entry.key, { state: 'uploaded', ref });
      return ref;
    } catch (err) {
      if (err instanceof UploadLinkError && err.status === 409) throw err;
      patchFile(entry.key, {
        state: 'failed',
        error: err instanceof Error ? err.message : 'Upload failed',
      });
      return null;
    }
  }

  async function handleSend() {
    if (files.length === 0) return;
    setError(null);
    setPhase('uploading');
    try {
      // One at a time: a guest on a phone connection gains nothing from five
      // uploads fighting for it, and the list shows where it has got to.
      const refs: (UploadedFileRef | null)[] = [];
      for (const entry of files) refs.push(await uploadOne(entry));
      if (refs.some((ref) => ref === null)) {
        setError('Some files did not upload. Try again, or remove them and send the rest.');
        setPhase('ready');
        return;
      }
      const result = await finalizeUpload(token, refs as UploadedFileRef[]);
      setSentNames(
        result.fileNames?.length ? result.fileNames : files.map((entry) => entry.file.name),
      );
      setPhase('done');
    } catch (err) {
      if (err instanceof UploadLinkError && err.status === 409) {
        setPhase('used');
        return;
      }
      /*
       * ⚠️ **A file the server no longer holds is uploaded again, not resent.**
       * Kept as uploaded, its id would go back on every Send and be refused
       * every time; dropping its presign and handle makes the next Send upload
       * its bytes afresh.
       */
      if (err instanceof UploadLinkError && err.missingFileIds.length > 0) {
        const missing = new Set(err.missingFileIds);
        setFiles((prev) =>
          prev.map((f) =>
            f.ref?.fileId && missing.has(f.ref.fileId)
              ? {
                  ...f,
                  state: 'failed',
                  ref: undefined,
                  ticket: undefined,
                  error: 'Did not arrive — press Send to upload it again',
                }
              : f,
          ),
        );
      }
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
      setPhase('ready');
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-8">
        {phase === 'loading' && (
          <div className="flex items-center justify-center py-10 text-gray-400">
            <Loader2 className="animate-spin" size={28} />
          </div>
        )}

        {phase === 'not-found' && (
          <div className="text-center py-6">
            <h1 className="text-lg font-semibold text-gray-900">Upload link not found</h1>
            <p className="mt-2 text-sm text-gray-500">
              This upload link is invalid or has expired. Please ask for a new one.
            </p>
          </div>
        )}

        {(phase === 'ready' || phase === 'uploading') && info && (
          <div>
            <UploadLinkHeader info={info} />

            {error && (
              <div
                role="alert"
                className="mt-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
              >
                {error}
              </div>
            )}

            <UploadDropZone
              accept={uploadAccept(allowedTypes) || undefined}
              multiple={maxFiles > 1}
              disabled={phase === 'uploading' || files.length >= maxFiles}
              onFiles={addFiles}
            />
            <p className="mt-2 text-center text-xs text-gray-400">
              Up to {maxFiles} file{maxFiles === 1 ? '' : 's'}, 25 MB each. No login needed.
            </p>

            {files.length > 0 && (
              <UploadFileList
                files={files}
                disabled={phase === 'uploading'}
                onRemove={removeFile}
              />
            )}

            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={phase === 'uploading' || files.length === 0}
              className="mt-5 w-full min-h-11 flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60"
              data-testid="upload-send"
            >
              {phase === 'uploading' ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Sending…
                </>
              ) : (
                <>
                  <Upload size={18} />
                  {files.length > 1 ? `Send ${files.length} files` : 'Send'}
                </>
              )}
            </button>
          </div>
        )}

        {phase === 'done' && (
          <div className="text-center py-4" data-testid="upload-success">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="text-green-600" size={24} />
            </div>
            <h1 className="text-lg font-semibold text-gray-900">
              {sentNames.length === 1 ? 'File received' : 'Files received'}
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Thanks — we received {sentNames.length === 1 ? 'it' : 'them'}. A confirmation has been
              emailed to you.
            </p>
            <ul className="mt-3 space-y-1 text-sm text-gray-700 text-left">
              {sentNames.map((name) => (
                <li key={name} className="flex items-center gap-2 min-w-0">
                  <CheckCircle2 size={14} className="text-green-600 flex-shrink-0" />
                  <span className="truncate font-medium">{name}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-gray-400">
              Need to send more? Ask us for a new upload link.
            </p>
          </div>
        )}

        {phase === 'used' && (
          <div className="text-center py-6" data-testid="upload-used">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="text-gray-500" size={24} />
            </div>
            <h1 className="text-lg font-semibold text-gray-900">Already uploaded</h1>
            <p className="mt-2 text-sm text-gray-600">
              This upload link has already been used. If you need to send more files, please ask us
              for a new link.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/** What is being asked for, by whom, about what, and where it will go. */
function UploadLinkHeader({ info }: { info: UploadLinkInfo }) {
  const title = info.title || info.label || 'Upload files';
  // The record itself — "Flowers · Cohen Wedding" — so a label shared by two
  // requests about one event still says which line or item this one is for.
  // Unless the heading already says it ("Files for …").
  const about =
    info.recordName && !title.includes(info.recordName)
      ? info.recordName
      : info.recordName
        ? ''
        : [info.vendorName, info.eventName].filter(Boolean).join(' — ');
  return (
    <div className="text-center">
      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-3">
        <FileUp className="text-primary-600" size={22} />
      </div>
      <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
      {info.requesterName && (
        <p className="mt-1 text-sm text-gray-600" data-testid="upload-requester">
          {info.requesterName} asked you for {title === info.label ? 'this' : 'these files'}.
        </p>
      )}
      {about && <p className="mt-1 text-sm text-gray-600">{about}</p>}
      {info.label && info.label !== title && (
        <p className="mt-1 text-xs text-gray-400">{info.label}</p>
      )}
      {info.destinationFolderName && (
        <p
          className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500"
          data-testid="upload-destination"
        >
          <FolderOpen size={12} /> Filed in {info.destinationFolderName}
        </p>
      )}
      <AcceptedFileTypesNote fileTypes={info.allowedFileTypes} />
    </div>
  );
}

/** Tap to choose, or drop files on it. */
function UploadDropZone({
  accept,
  multiple,
  disabled,
  onFiles,
}: {
  accept: string | undefined;
  multiple: boolean;
  disabled: boolean;
  onFiles: (files: File[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          if (!disabled && e.dataTransfer.files.length > 0) {
            onFiles(Array.from(e.dataTransfer.files));
          }
        }}
        className={`mt-5 w-full min-h-11 border-2 border-dashed rounded-xl px-4 py-6 text-center transition-colors disabled:opacity-60 ${
          dragActive
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-primary-400'
        }`}
        data-testid="upload-dropzone"
      >
        <Upload size={20} className="mx-auto text-gray-400" />
        <span className="mt-2 block text-sm text-gray-700">
          Choose {multiple ? 'files' : 'a file'}{' '}
          <span className="text-primary-600">or drop {multiple ? 'them' : 'it'} here</span>
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        className="hidden"
        data-testid="upload-input"
        onChange={(e) => {
          const chosen = Array.from(e.target.files ?? []);
          if (chosen.length > 0) onFiles(chosen);
          e.target.value = '';
        }}
      />
    </>
  );
}

const STATE_LABEL: Record<FileState, string> = {
  waiting: 'Ready',
  uploading: 'Uploading…',
  uploaded: 'Uploaded',
  failed: 'Failed',
};

/** The files chosen so far, each with its size, where it has got to, and a remove. */
function UploadFileList({
  files,
  disabled,
  onRemove,
}: {
  files: PendingFile[];
  disabled: boolean;
  onRemove: (key: string) => void;
}) {
  return (
    <ul className="mt-4 divide-y divide-gray-100 border border-gray-200 rounded-xl">
      {files.map((entry) => (
        <li
          key={entry.key}
          className="flex items-center gap-3 px-3 py-2"
          data-testid="upload-file-row"
          data-state={entry.state}
        >
          <FileText size={16} className="text-gray-400 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-800">{entry.file.name}</p>
            <p className="text-xs text-gray-500">
              {formatFileSize(entry.file.size)} ·{' '}
              <FileStateLabel state={entry.state} error={entry.error} />
            </p>
          </div>
          <button
            type="button"
            aria-label={`Remove ${entry.file.name}`}
            disabled={disabled}
            onClick={() => onRemove(entry.key)}
            className="min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-gray-50 disabled:opacity-40"
          >
            <X size={16} />
          </button>
        </li>
      ))}
    </ul>
  );
}

function FileStateLabel({ state, error }: { state: FileState; error?: string }) {
  if (state === 'uploading') {
    return (
      <span className="inline-flex items-center gap-1 text-primary-600">
        <Loader2 size={11} className="animate-spin" /> {STATE_LABEL[state]}
      </span>
    );
  }
  if (state === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 text-red-600">
        <AlertCircle size={11} /> {error ?? STATE_LABEL[state]}
      </span>
    );
  }
  if (state === 'uploaded') return <span className="text-green-600">{STATE_LABEL[state]}</span>;
  return <span>{STATE_LABEL[state]}</span>;
}
